<template>
  <q-card flat style="max-width: 600vw; max-height: 80vh">
    <q-card-section>
      <div class="text-h6 row items-center">Bulk Import</div>
      <div class="q-mt-xs text-grey-7">
        Take advantage of our bulk upload feature to quickly import multiple properties from a CSV
        file and save valuable time.
      </div>
    </q-card-section>
    <q-separator />
    <q-card-section class="row justify-between items-center q-col-gutter-md">
      <div class="col">
        <div class="text-weight-medium">CSV Template</div>
        <div class="text-grey-7">
          Download the CSV template and fill in the required columns to bulk import your properties.
          <span class="text-bold cursor-pointer">
            Tips
            <q-menu transition-duration="100">
              <ul>
                <li v-for="tip in tips" :key="tip">{{ tip }}</li>
              </ul>
            </q-menu>
          </span>
        </div>
      </div>
      <div>
        <q-btn
          :href="sampleCSVURL"
          download="hellodata-bulk-upload-template.csv"
          label="Download"
          no-caps
          outline
          unelevated
        />
      </div>
    </q-card-section>
    <q-separator />
    <q-card-section class="row justify-between items-center">
      <div class="q-mb-sm">
        <div class="text-weight-medium">Upload Data CSV</div>
        <div class="text-grey-7">Upload the Data CSV file to import addresses.</div>
      </div>
      <q-file
        v-model="file"
        accept=".csv"
        color="primary"
        dense
        label="Select a CSV file"
        outlined
        style="min-width: 300px"
        @update:model-value="uploaded"
      />
    </q-card-section>
    <q-separator />
    <template v-if="errors.length">
      <q-card-section>
        <div class="text-weight-medium">Errors</div>
        <ul class="text-negative" style="max-height: 100px; overflow-y: auto">
          <li v-for="err in errors" :key="err">{{ err }}</li>
        </ul>
      </q-card-section>
      <q-separator />
    </template>
    <q-card-section v-if="rows.length > 0" class="scroll" style="max-height: 400px">
      <div class="text-weight-medium q-mb-md">Confirm Selection</div>
      <div v-if="rows.length > 0" class="q-gutter-y-sm">
        <q-virtual-scroll v-slot="{ item }" :item-size="10" :items="rows">
          <q-card bordered class="q-mb-md" flat>
            <div v-for="(p, pIdx) in [item.subject].concat(item.comps)" :key="pIdx">
              <div class="row justify-between items-center">
                <div class="q-pa-sm">
                  {{ p.query }}
                  <span v-if="pIdx === 0">(Subject)</span>
                  <span v-else>(Comparable {{ pIdx }})</span>
                </div>
                <div v-if="p.tags?.length && pIdx === 0">
                  <q-badge
                    v-for="tag in p.tags"
                    :key="tag"
                    class="q-mr-sm cursor-pointer"
                    color="accent"
                    rounded
                  >
                    {{ tag }}
                  </q-badge>
                </div>
              </div>
              <q-separator />
              <!-- Let users pick the right response (or none) -->
              <div class="row q-gutter-col-md q-pa-sm">
                <q-radio
                  v-for="prop in foundProperties[p.query]"
                  :key="prop.id"
                  v-model="selectedProperties[p.query]"
                  :val="prop.id"
                  class="col-6 q-mb-sm"
                  color="green"
                  dense
                >
                  <div v-if="prop.building_name" class="text-weight-medium">
                    {{ prop.building_name }}
                  </div>
                  {{ prop.street_address }}
                  <div class="text-grey-7">
                    {{ prop.city }}, {{ prop.state }} {{ prop.zip_code }}
                  </div>
                  <div
                    v-if="prop.number_units || prop.year_built"
                    class="text-grey-7 q-gutter-x-sm"
                  >
                    <span v-if="prop.number_units" class="text-grey-7"
                      >Units: {{ prop.number_units }}</span
                    >
                    <span v-if="prop.year_built" class="text-grey-7"
                      >Year Built: {{ prop.year_built }}</span
                    >
                  </div>
                </q-radio>
                <q-radio
                  v-model="selectedProperties[p.query]"
                  :val="null"
                  class="col-6"
                  color="grey"
                  dense
                  label="Do Not Import"
                />
              </div>
            </div>
            <q-separator v-if="item.comps.length === 0" />
            <div v-if="item.comps.length === 0" class="q-pa-sm">
              <q-icon class="q-mr-sm" color="accent" name="info" />
              <span class="q-pa-sm text-grey-7">We'll recommend comps for you</span>
            </div>
          </q-card>
        </q-virtual-scroll>
      </div>
    </q-card-section>
    <q-separator />
    <q-linear-progress
      v-if="loadingPropertiesProgress"
      :value="loadingPropertiesProgress"
      color="accent"
    />
    <q-linear-progress
      v-if="generatingReportsProgress || uploadStarted"
      :value="generatingReportsProgress || 0"
      color="primary"
    />
    <q-card-actions align="right">
      <q-btn v-close-popup label="Close" no-caps unelevated />
      <q-btn
        v-if="validImports"
        :label="`Generate ${validImports} Reports`"
        :loading="
          (loadingPropertiesProgress !== null && loadingPropertiesProgress > 0) ||
          (generatingReportsProgress !== null && generatingReportsProgress > 0) ||
          uploadStarted
        "
        color="primary"
        no-caps
        unelevated
        @click="generateReports"
      />
    </q-card-actions>
  </q-card>
</template>

<script lang="ts">
import Papa from 'papaparse'
import { chunkedMapPromiseAll, OrganizationVisibility, type PropertySearchResponse } from 'common'
import { usePropertyStore } from '@/stores/property'
import { usePropertyReportStore } from '@/stores/propertyreport'
import { useUserStore } from '@/stores/user'
import { compResponseToSearch } from '@/utils'

interface CSVRow {
  idx: number
  group_id: string | undefined
  type: 'subject' | 'comparable'
  query: string
  zip_code?: string
  state?: string
  tags?: string[]
  lat?: number
  lon?: number
  report_name?: string
  company_wide?: boolean
}

const validifyZipCode = (zip: string): string | null => {
  // takes the given string, and returns a 5-digit zip code if possible (append 0 if 4 digits, or remove if ZIP+4 format)
  // use regex
  if (zip.match(/^\d{5}$/)) {
    return zip
  } else if (zip.match(/^\d{4}$/)) {
    return '0' + zip
  } else if (zip.match(/^\d{5}-\d{4}$/)) {
    return zip.split('-')[0]
  }
  return null
}

export default {
  name: 'BulkCsvImport',
  components: {},
  emits: ['finished'],
  data() {
    return {
      file: null as File | null,
      reports: {} as { [key: string]: CSVRow[] },
      errors: [] as string[],
      tips: [
        'The group_id column is optional. If not provided, it will be auto-generated. It is used to group properties under the same report.',
        'The type column must be "subject" or "comparable".',
        'The query column is required. It must represent an address or property name. Incomplete queries can work, but may not be accurate.',
        'The zip_code column helps with accuracy but is optional. If provided, it must be 5 digits (eg: 60601 or 00601).',
        'The state column helps with accuracy but is optional. If provided, it must be 2 characters (eg: CA).'
      ],
      foundProperties: {} as { [key: string]: PropertySearchResponse[] },
      selectedProperties: {} as { [key: string]: string | null },
      completedReports: 0,
      sampleCSVURL:
        'https://storage.googleapis.com/hello-data-ai-public-assets/hellodata-bulk-upload-template.csv',
      uploadStarted: false
    }
  },
  computed: {
    countProperties(): number {
      // count the number of unique properties
      const queries = Object.values(this.reports)
        .flat()
        .map((row: CSVRow) => row.query)
      return new Set(queries).size
    },
    countFoundProperties(): number {
      return Object.keys(this.foundProperties).length
    },
    loadingPropertiesProgress(): number | null {
      if (
        this.countProperties === 0 ||
        this.countFoundProperties === 0 ||
        this.countProperties === this.countFoundProperties
      ) {
        return null
      }
      return this.countFoundProperties / this.countProperties
    },
    generatingReportsProgress(): number | null {
      return this.rows.length > 0 ? this.completedReports / this.rows.length : null
    },
    validImports(): number {
      // count the number of reports that have a valid subject with a key in the selectedProperties
      return Object.keys(this.reports).reduce((acc, key) => {
        const subject = this.reports[key].find((row) => row.type === 'subject')
        if (subject === undefined) {
          return acc
        }

        return acc + (this.selectedProperties[subject.query] !== null ? 1 : 0)
      }, 0)
    },
    rows(): { key: string; subject: CSVRow; comps: CSVRow[] }[] {
      const values: { key: string; subject: CSVRow; comps: CSVRow[] }[] = []

      for (const key in this.reports) {
        const subject = this.reports[key].find((row) => row.type === 'subject')
        if (subject === undefined) {
          console.warn(`the row with key ${key} does not have a subject`)
          continue
        }

        const comps = this.reports[key].filter((row) => row.type === 'comparable')
        values.push({ key, subject, comps })
      }

      return values
    },
    availableReportTags() {
      return useUserStore().availableReportTags
    }
  },
  methods: {
    validateAndFilterTags(tags: string[], rowIndex: number): string[] {
      const existingTags = this.availableReportTags
      const validTags: string[] = []
      const invalidTags: string[] = []

      tags.forEach((tag) => {
        if (existingTags.includes(tag)) {
          validTags.push(tag)
        } else {
          invalidTags.push(tag)
        }
      })

      if (invalidTags.length > 0) {
        this.errors.push(
          `Row ${rowIndex}: The following tags do not exist in your organization: ${invalidTags.join(', ')}. Only existing tags will be used. You can ask an admin to add them in the organization settings.`
        )
      }

      return validTags
    },
    async generateReports() {
      // restart the processing state
      this.completedReports = 0
      this.uploadStarted = true

      try {
        for (const { subject, comps } of this.rows) {
          const selectedID = this.selectedProperties[subject.query]
          if (selectedID === null) {
            // the user has not selected a property to import for this subject
            // we skip this report
            continue
          }

          const selectedProperty = this.foundProperties[subject.query].find(
            (prop) => prop.id === selectedID
          )

          if (selectedProperty === undefined) {
            console.error('Could not find selected property, but it should have been found')
            continue
          }

          const providedComps = comps.flatMap((comp) => {
            const selectedID = this.selectedProperties[comp.query]
            if (selectedID === null) {
              return []
            }

            const selectedComp = this.foundProperties[comp.query].find(
              (prop) => prop.id === selectedID
            )

            if (selectedComp === undefined) {
              console.error('Could not find selected property, but it should have been found')
              return []
            }

            return selectedComp
          })

          if (providedComps.length === 0) {
            const retrievedSubject = await usePropertyStore().details(selectedProperty.id)

            // we look for comps ourselves
            await usePropertyStore()
              .comparables(retrievedSubject, {
                excludedIds: [],
                selectedIds: [],
                topN: 10
              })
              .then((resp) => {
                if (resp.comparables.length === 0) {
                  console.error('No comparables found for subject; skipping report')
                  this.completedReports += 1
                  return
                }

                return usePropertyReportStore()
                  .create({
                    name:
                      subject.report_name ||
                      retrievedSubject.building_name ||
                      retrievedSubject.street_address,
                    subject: selectedProperty,
                    comparable_addresses: resp.comparables.map(compResponseToSearch),
                    excluded_property_ids: [],
                    is_organization_visible: subject.company_wide ?? false, // TODO: Delete me
                    organization_visibility: subject.company_wide
                      ? OrganizationVisibility.EDITOR
                      : null,
                    tags: subject.tags?.length ? subject.tags : ['Active'],
                    view_preferences: []
                  })
                  .finally(() => {
                    this.completedReports += 1
                  })
              })
          } else {
            await usePropertyReportStore()
              .create({
                name:
                  subject.report_name ||
                  selectedProperty.building_name ||
                  selectedProperty.street_address,
                subject: selectedProperty,
                comparable_addresses: providedComps,
                excluded_property_ids: [],
                is_organization_visible: subject.company_wide ?? false, // TODO: Delete me
                organization_visibility: subject.company_wide
                  ? OrganizationVisibility.EDITOR
                  : null,
                tags: subject.tags?.length ? subject.tags : ['Active'],
                view_preferences: []
              })
              .finally(() => {
                this.completedReports += 1
              })
          }
        }
      } catch (e) {
        console.error(e)
      } finally {
        this.$tracker.bulkImportedCSV(this.completedReports)
        this.$emit('finished', this.completedReports)
        this.completedReports = 0
        this.uploadStarted = false
        this.reports = {}
        this.foundProperties = {}
        this.selectedProperties = {}
      }
    },
    async findProperties() {
      const store = usePropertyStore()

      const retrieveData = (row: CSVRow) => {
        if (this.foundProperties[row.query]) {
          return Promise.resolve()
        }

        let lat: number | undefined
        let lon: number | undefined
        let maxDistance: number | undefined
        if (row.lat !== undefined && row.lon !== undefined) {
          lat = row.lat
          lon = row.lon
          maxDistance = 0.5
        }

        return store
          .propertySearch(
            row.query,
            { lat, lon, zipCode: row.zip_code, state: row.state, maxDistance: maxDistance },
            false
          )
          .then((foundProps) => {
            // take top 3 results
            this.foundProperties[row.query] = foundProps.slice(0, 3)

            // select the first result by default
            if (foundProps.length > 0) {
              this.selectedProperties[row.query] = foundProps[0].id
            } else {
              this.selectedProperties[row.query] = null
            }
          })
          .catch(() => {
            this.foundProperties[row.query] = []
          })
      }

      await chunkedMapPromiseAll(Object.values(this.reports).flat(), 20, retrieveData)
    },
    uploaded() {
      // reset the state
      this.reports = {}
      this.foundProperties = {}
      this.errors = []

      // read the address and zip_code columns from the csv file
      if (!this.file) return

      Papa.parse(this.file, {
        header: true,
        skipEmptyLines: true,
        complete: (results: {
          data: Record<string, unknown>[]
          errors: { row: number; message: string }[]
        }) => {
          this.errors = results.errors.map((err) => `Row ${err.row}: ${err.message}`)
          if (results.data.length === 0) {
            this.errors.push('CSV file is empty')
            return
          }

          const requiredColumns = [
            'query',
            'zip_code',
            'group_id',
            'type',
            'state',
            'tags',
            'lat',
            'lon',
            'report_name',
            'company_wide'
          ] as (keyof CSVRow)[]

          for (const col of requiredColumns) {
            if (results.data[0][col] === undefined) {
              this.errors.push(`CSV file must have column: ${col}`)
              return
            }
          }

          const reports: { [key: string]: CSVRow[] } = {}
          let autoGroupID = 0

          for (let i = 0; i < results.data.length; i++) {
            const row = results.data[i] as unknown as CSVRow // we have verified that the required columns exist before
            if (!row.query) {
              this.errors.push(`Row ${i}: query is required`)
              continue
            }
            const parsedZip = validifyZipCode(row.zip_code || '')
            if (parsedZip !== null) {
              row.zip_code = parsedZip
            } else {
              if (row.zip_code) {
                this.errors.push(
                  `Row ${i}: zip_code must be 5 digits (eg: 60601 or 00601). Not using zip_code ${row.zip_code}`
                )
              }
              row.zip_code = undefined
            }
            if (row.state && row.state.length !== 2) {
              this.errors.push(
                `Row ${i}: state must be 2 characters (eg: CA). Not using state ${row.state}`
              )
              row.state = undefined
            }
            row.type = row.type.toLowerCase()
            if (row.type !== 'subject' && row.type !== 'comparable') {
              this.errors.push(
                `Row ${i}: type must be 'subject' or 'comparable'. Not using type ${row.type}`
              )
              continue
            }
            if (!row.group_id) {
              row.group_id = 'autogroup-' + autoGroupID.toString()
              autoGroupID += 1
            }
            if (!reports[row.group_id]) {
              reports[row.group_id] = []
            }

            let rawTags = []
            if (Array.isArray(row.tags)) {
              rawTags = row.tags.filter((tag) => tag && tag.trim() !== '')
            } else if (typeof row.tags === 'string') {
              rawTags = (row.tags as string)
                .split(',')
                .map((tag) => tag.trim())
                .filter((tag) => tag !== '')
            }

            // Validate tags and filter out any that don't exist in the organization
            const validTags = this.validateAndFilterTags(rawTags, i)

            const companyWide =
              typeof row.company_wide === 'string'
                ? (row.company_wide as string).toLowerCase() === 'true'
                : Boolean(row.company_wide)

            reports[row.group_id].push({
              idx: i,
              group_id: row.group_id,
              type: row.type,
              query: row.query,
              zip_code: row.zip_code,
              state: row.state ? row.state : undefined,
              tags: validTags,
              lat: row.lat ? row.lat : undefined,
              lon: row.lon ? row.lon : undefined,
              report_name: row.report_name,
              company_wide: companyWide
            })
          }

          // check if all reports have exactly 1 subject
          for (const key in reports) {
            const report = reports[key]
            const subjectCount = report.filter((row) => row.type === 'subject').length
            if (subjectCount !== 1) {
              this.errors.push(
                `Property on group_id ${key} has ${subjectCount} subjects. It must have exactly 1 subject`
              )
            }
            // remove reports that have multiple subjects
            if (subjectCount !== 1) {
              delete reports[key]
            }
          }

          this.reports = reports
          this.findProperties().catch(console.error)
        }
      })
    }
  }
}
</script>
